import React, { useEffect, useState } from "react";

import { DeleteOutlined, ExclamationCircleOutlined, ExportOutlined, MoreOutlined, } from "@ant-design/icons";

import { Button, Dropdown, Menu, Modal, notification, Space, Spin, } from "antd";
import { IntlShape, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { AccountLayout } from "components/store-layout/AccountLayout";
import { AccountPageHeader } from "toolkit/pageHeader/AccountPageHeader";

import { ItemType } from "antd/lib/menu/hooks/useItems";
import { AccountCard } from "toolkit/card/AccountCard";
import { useForm } from "antd/lib/form/Form";
import {
  AccountInfo,
  DeleteAccount,
  deleteAccount,
  exportAccountInfo,
  getAccountInfo,
  getLinkedAccount,
  LinkedAccount,
  updateAccountInfo
} from "services/rest/request";

import { FormInstance } from "rc-field-form";
import { ChangePassword } from "./change-password/ChangePassword";
import { ConnectAccounts } from "./connect-accounts/ConnectAccounts";
import { ContactInfo } from "./contact-info/ContactInfo";

import { isRu } from "services/region/region";
import "./AccountSettings.less";
import { AxiosResponse } from "axios";

export function AccountSettings() {
  const navigate = useNavigate();
  const intl = useIntl();

  const [isChangedForm, setChangedForm] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isDeleteLoading, setDeleteLoading] = useState(false);
  const [connections, setConnections] = useState<LinkedAccount[]>([]);

  const [form] = useForm<AccountInfo>();

  useEffect(() => {
    update(form).then(() => setLoading(false));
    // Social identity providers are not available on the ru instance (RKN)
    if (!isRu()) {
      getConnections().then(data => {
        setConnections(data);
      });
    }
  }, []);

  const externalMenuItems: ItemType[] = [
    {
      key: "export",
      className: "account-settings__export",
      icon: <ExportOutlined />,
      onClick: exportAccountInfo,
      label: intl.formatMessage({ id: "jmix-store.account-settings.export-personal-data" })
    },
    {
      key: "delete",
      className: "account-settings__delete",
      icon: <DeleteOutlined />,
      onClick: () => {
        Modal.confirm({
          centered: true,
          icon: <ExclamationCircleOutlined className="account-settings__delete-profile-icon" />,
          title: intl.formatMessage({ id: "jmix-account.account-settings.delete-profile.title" }),
          content: (
            <p>
              {intl.formatMessage({ id: "jmix-account.account-settings.delete-profile.description" })}
            </p>
          ),
          okButtonProps: {
            loading: isDeleteLoading,
          },
          onOk() {
            setDeleteLoading(true);
            deleteAccount()
              .then((resp: AxiosResponse<DeleteAccount>) => {
                setDeleteLoading(false);
                const data = resp.data;
                if (data.success) {
                  navigate("/success");
                } else {
                  Modal.warning({
                    centered: true,
                    title: intl.formatMessage({ id: "jmix-account.account-settings.delete-account.title" }),
                    content: (
                      <div>
                        <p>{intl.formatMessage({ id: "jmix-account.account-settings.delete-account.description_1" })}</p>
                        <p>{intl.formatMessage({ id: "jmix-account.account-settings.delete-account.description_2" }, {
                          time: <strong>{getFormatTime(resp.data.repeatIn)}</strong>
                        })}</p>
                      </div>
                    ),
                  });
                }

              })
              .catch((error) => {
                setDeleteLoading(false);
                notification.error({
                  message: intl.formatMessage({ id: "jmix-account.account-settings.delete-account.error" }),
                  placement: "bottomLeft",
                });
              });
          },
        });
      },
      label: intl.formatMessage({ id: "jmix-store.account-settings.delete-profile" })
    }
  ];

  const dropdown = (
    <Dropdown
      overlay={<Menu items={externalMenuItems} />}
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );

  return (
    <>
      <AccountPageHeader
        title={intl.formatMessage({ id: "jmix-account.account-settings.title" })}
        extra={dropdown}
      />
      <AccountLayout>
        <Spin spinning={isLoading}>
          <AccountCard className="contact-info__card">
            <div className="account-settings__content">
              <div>
                <ContactInfo
                  form={form}
                  onChange={() => setChangedForm(true)}
                />
              </div>
              {!isRu() && (
                <div className="account-settings__wrapper">
                  <ConnectAccounts connections={connections} />
                </div>
              )}
              <div>
                <ChangePassword />
              </div>
              <Space>
                <Button
                  type="primary"
                  disabled={!isChangedForm || isLoading}
                  onClick={() =>
                    form.validateFields()
                      .then(() => {
                          setLoading(true);

                          save(form, intl)
                            .then(() => {
                              setLoading(false);
                              setChangedForm(false);
                            })
                            .catch(() => setLoading(false));
                        }
                      )
                  }
                >
                  {intl.formatMessage({ id: "jmix-account.account-settings.save-button" })}
                </Button>
                <Button
                  disabled={!isChangedForm || isLoading}
                  onClick={() => {
                    setLoading(true);

                    update(form).then(() => {
                      setChangedForm(false);
                      setLoading(false);
                    });
                  }}>
                  {intl.formatMessage({ id: "jmix-account.account-settings.cancel-button" })}
                </Button>
              </Space>
            </div>
          </AccountCard>
        </Spin>
      </AccountLayout>
    </>
  );
}

async function save(form: FormInstance<AccountInfo>, intl: IntlShape) {
  const isSuccess = await updateAccountInfo(form.getFieldsValue());

  if (isSuccess) {
    notification["success"]({
      placement: "top",
      message: intl.formatMessage({ id: "jmix-account.account-settings.save.success.title" }),
      description: intl.formatMessage({ id: "jmix-account.account-settings.save.success.description" }),
    });
  } else {
    notification["error"]({
      placement: "top",
      message: intl.formatMessage({ id: "jmix-account.account-settings.save.error.title" }),
      description: intl.formatMessage({ id: "jmix-account.account-settings.save.error.description" }),
    });
  }
}

async function update(form: FormInstance<AccountInfo>) {
  const info = await getAccountInfo();
  form.setFieldsValue(info);
}

async function getConnections() {
  const connections = await getLinkedAccount();
  return connections;
}

function getFormatTime(value: number) {
  const min = Math.floor(value / 60);
  const sec = value % 60;

  return `${min}:${sec}`;
}
